import { type ColorValue } from './color';

/**
 * unique colormap const
 */
export const uniqueColorMap = {
  transparent: 'transparent',
  aliceblue: '#f0f8ff',
  antiquewhite: '#faEbd7',
  aqua: '#00ffff',
  aquamarine: '#7fffd4',
  azure: '#f0ffff',
  beige: '#f5f5dc',
  bisque: '#ffE4c4',
  black: '#000000',
  blanchedalmond: '#ffEbcd',
  blue: '#0000ff',
  blueviolet: '#8a2bE2',
  brown: '#a52a2a',
  burlywood: '#dEb887',
  cadetblue: '#5f9Ea0',
  chartreuse: '#7fff00',
  chocolate: '#d2691E',
  coral: '#ff7f50',
  cornflowerblue: '#6495Ed',
  cornsilk: '#fff8dc',
  crimson: '#dc143c',
  darkblue: '#00008b',
  darkcyan: '#008b8b',
  darkgoldenrod: '#b8860b',
  darkgray: '#a9a9a9',
  darkgreen: '#006400',
  darkkhaki: '#bdb76b',
  darkmagenta: '#8b008b',
  darkolivegreen: '#556b2f',
  darkorange: '#ff8c00',
  darkorchid: '#9932cc',
  darkred: '#8b0000',
  darksalmon: '#E9967a',
  darkseagreen: '#8fbc8f',
  darkslateblue: '#483d8b',
  darkslategray: '#2f4f4f',
  darkturquoise: '#00cEd1',
  darkviolet: '#9400d3',
  deeppink: '#ff1493',
  deepskyblue: '#00bfff',
  dimgray: '#696969',
  dodgerblue: '#1E90ff',
  firebrick: '#b22222',
  floralwhite: '#fffaf0',
  forestgreen: '#228b22',
  gainsboro: '#dcdcdc',
  ghostwhite: '#f8f8ff',
  gold: '#ffd700',
  goldenrod: '#daa520',
  gray: '#808080',
  green: '#008000',
  greenyellow: '#adff2f',
  honeydew: '#f0fff0',
  hotpink: '#ff69b4',
  indianred: '#cd5c5c',
  indigo: '#4b0082',
  ivory: '#fffff0',
  khaki: '#f0E68c',
  lavender: '#E6E6fa',
  lavenderblush: '#fff0f5',
  lawngreen: '#7cfc00',
  lemonchiffon: '#fffacd',
  lightblue: '#add8E6',
  lightcoral: '#f08080',
  lightcyan: '#E0ffff',
  lightgoldenrodyellow: '#fafad2',
  lightgray: '#d3d3d3',
  lightgreen: '#90EE90',
  lightpink: '#ffb6c1',
  lightsalmon: '#ffa07a',
  lightseagreen: '#20b2aa',
  lightskyblue: '#87cEfa',
  lightslategray: '#778899',
  lightsteelblue: '#b0c4dE',
  lightyellow: '#ffffE0',
  lime: '#00ff00',
  limegreen: '#32cd32',
  linen: '#faf0E6',
  magenta: '#ff00ff',
  maroon: '#800000',
  mediumaquamarine: '#66cdaa',
  mediumblue: '#0000cd',
  mediumorchid: '#ba55d3',
  mediumpurple: '#9370db',
  mediumseagreen: '#3cb371',
  mediumslateblue: '#7b68EE',
  mediumspringgreen: '#00fa9a',
  mediumturquoise: '#48d1cc',
  mediumvioletred: '#c71585',
  midnightblue: '#191970',
  mintcream: '#f5fffa',
  mistyrose: '#ffE4E1',
  moccasin: '#ffE4b5',
  navajowhite: '#ffdEad',
  navy: '#000080',
  oldlace: '#fdf5E6',
  olive: '#808000',
  olivedrab: '#6b8E23',
  orange: '#ffa500',
  orangered: '#ff4500',
  orchid: '#da70d6',
  palegoldenrod: '#EEE8aa',
  palegreen: '#98fb98',
  paleturquoise: '#afEEEE',
  palevioletred: '#db7093',
  papayawhip: '#ffEfd5',
  peachpuff: '#ffdab9',
  peru: '#cd853f',
  pink: '#ffc0cb',
  plum: '#dda0dd',
  powderblue: '#b0E0E6',
  purple: '#800080',
  rebeccapurple: '#663399',
  red: '#ff0000',
  rosybrown: '#bc8f8f',
  royalblue: '#4169E1',
  saddlebrown: '#8b4513',
  salmon: '#fa8072',
  sandybrown: '#f4a460',
  seagreen: '#2E8b57',
  seashell: '#fff5EE',
  sienna: '#a0522d',
  silver: '#c0c0c0',
  skyblue: '#87cEEb',
  slateblue: '#6a5acd',
  slategray: '#708090',
  snow: '#fffafa',
  springgreen: '#00ff7f',
  steelblue: '#4682b4',
  tan: '#d2b48c',
  teal: '#008080',
  thistle: '#d8bfd8',
  tomato: '#ff6347',
  turquoise: '#40E0d0',
  violet: '#EE82EE',
  wheat: '#f5dEb3',
  white: '#ffffff',
  whitesmoke: '#f5f5f5',
  yellow: '#ffff00',
  yellowgreen: '#9acd32'
};

export const defaultRedwoodNoFillPaletteValues: Array<ColorValue> = [
  'transparent',
  '#FFFFFF',
  '#F4F4F4',
  '#D5D5D5',
  '#909090',
  '#4B4B4B',
  '#000000',
  '#E6B0AA',
  '#EDBB99',
  '#F9E79F',
  '#A9DFBF',
  '#AED6F1',
  '#D7BDE2',
  '#F9B3E2',
  '#CD6155',
  '#F98C45',
  '#F4D03F',
  '#52BE80',
  '#5DADE2',
  '#AF7AC5',
  '#E55FB9',
  '#81261D',
  '#853500',
  '#A3850A',
  '#19713E',
  '#246A98',
  '#6C3E7F',
  '#932970'
] as const;

export const defaultRedwoodPaletteValues: Array<ColorValue> = [
  '#FFFFFF',
  '#F4F4F4',
  '#D5D5D5',
  '#909090',
  '#6E6E6E',
  '#4B4B4B',
  '#000000',
  '#E6B0AA',
  '#EDBB99',
  '#F9E79F',
  '#A9DFBF',
  '#AED6F1',
  '#D7BDE2',
  '#F9B3E2',
  '#CD6155',
  '#F98C45',
  '#F4D03F',
  '#52BE80',
  '#5DADE2',
  '#AF7AC5',
  '#E55FB9',
  '#81261D',
  '#853500',
  '#A3850A',
  '#19713E',
  '#246A98',
  '#6C3E7F',
  '#932970'
];

export type NamedColor = keyof typeof uniqueColorMap;

/**
 * Returns a color definition string from the named color.
 * @param {String} name  The color name.
 * @returns A hex or RGBA color string.
 */
export const getNamedColor = (name: string) =>
  uniqueColorMap[name.trim().toUpperCase() as NamedColor];

export const uniqueColorValues = Object.values(uniqueColorMap);
